import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { hapticFeedback } from '@telegram-apps/sdk-react';
import { BingoCardView } from '@/components/BingoCardView';
import { ConnectedBar } from '@/components/ConnectedBar';
import { GameResultModal, type GameResult } from '@/components/GameResultModal';
import { LastCalls, MasterBoard } from '@/components/MasterBoard';
import { NotAWinnerModal } from '@/components/NotAWinnerModal';
import { ballLabel, gameStatusLine, letterForNumber } from '@/lib/bingo';
import { api } from '@/lib/api';
import { useGameLive } from '@/lib/useGameLive';
import { etbInt } from '@/lib/money';
import { serverNow } from '@/lib/serverClock';
import type { ClaimResponse, DrawSnapshot, GameDetail, Me, Ticket } from '@/lib/types';

export function GameRoom() {
    const { gameId } = useParams<{ gameId: string }>();
    const numericGameId = gameId ? Number(gameId) : null;
    const navigate = useNavigate();

    const [me, setMe] = useState<Me | null>(null);
    const [game, setGame] = useState<GameDetail | null>(null);
    const [tickets, setTickets] = useState<Ticket[] | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [claiming, setClaiming] = useState<string | null>(null);
    const [claimResult, setClaimResult] = useState<ClaimResponse | null>(null);
    const [resultModal, setResultModal] = useState<GameResult | null>(null);
    const [loadingResult, setLoadingResult] = useState(false);
    const [winnerSnapshot, setWinnerSnapshot] = useState<DrawSnapshot['winner']>(null);
    const [soundOn, setSoundOn] = useState(true);
    const [now, setNow] = useState(() => serverNow());
    const [leaving, setLeaving] = useState(false);
    const [notWinnerMessage, setNotWinnerMessage] = useState<string | null>(null);
    // Manual daub: each ticket keeps its own set of numbers the player has
    // tapped. Nothing is marked automatically when a number is called --
    // the player has to do it themselves, same as a real cartela.
    const [markedByTicket, setMarkedByTicket] = useState<Record<string, Set<number>>>({});

    const toggleMark = (ticketId: string, n: number): void => {
        try {
            hapticFeedback.selectionChanged();
        } catch {
            // ignore
        }
        setMarkedByTicket((prev) => {
            const current = new Set(prev[ticketId] ?? []);
            if (current.has(n)) {
                current.delete(n);
            } else {
                current.add(n);
            }
            return { ...prev, [ticketId]: current };
        });
    };

    const { snapshot, error: liveError, socketState } = useGameLive(numericGameId);

    useEffect(() => {
        const id = window.setInterval(() => setNow(serverNow()), 250);
        return () => window.clearInterval(id);
    }, []);

    const refreshMeta = async (): Promise<void> => {
        if (numericGameId === null) return;
        const [meRes, gameRes, ticketsRes] = await Promise.all([
            api.get<{ data: Me }>('/me'),
            api.get<{ data: GameDetail }>(`/games/${numericGameId}`),
            api.get<{ data: Ticket[] }>('/me/tickets', { params: { game_id: numericGameId } }),
        ]);
        setMe(meRes.data.data);
        setGame(gameRes.data.data);
        setTickets(ticketsRes.data.data);
    };

    useEffect(() => {
        if (numericGameId === null) return;
        let cancelled = false;

        refreshMeta().catch((e) => {
            if (!cancelled) setError(extractMessage(e));
        });
        // Poll often while status is still "open" after countdown (triggers maybeStart).
        const id = window.setInterval(() => {
            void refreshMeta().catch(() => undefined);
        }, 1000);

        return () => {
            cancelled = true;
            window.clearInterval(id);
        };
    }, [numericGameId]);

    const drawnSet = useMemo<Set<number>>(() => new Set(snapshot?.numbers ?? []), [snapshot?.numbers]);
    const lastBall = snapshot?.numbers.length ? snapshot.numbers[snapshot.numbers.length - 1] : null;
    const status = snapshot?.status ?? game?.status;
    const canClaim = status === 'active' || status === 'paused';
    const connected = socketState === 'connected';

    useEffect(() => {
        if (game?.status !== 'completed' && snapshot?.status !== 'completed') return;
        void api
            .get<{ data: DrawSnapshot }>(`/games/${numericGameId}/snapshot`)
            .then((res) => setWinnerSnapshot(res.data.data.winner ?? null))
            .catch(() => undefined);
    }, [game?.status, snapshot?.status, numericGameId]);

    useEffect(() => {
        const done = game?.status === 'completed' || snapshot?.status === 'completed';
        if (!done || resultModal !== null) return;

        setLoadingResult(true);
        const t = window.setTimeout(() => {
            const wonTicket = tickets?.find((tk) => tk.status === 'won');
            const userWon =
                claimResult?.status === 'approved' ||
                claimResult?.status === 'paid' ||
                wonTicket !== undefined;

            const winner = winnerSnapshot ?? snapshot?.winner ?? null;
            setResultModal({
                won: userWon,
                winnerName: userWon ? (me?.name ?? 'You') : (winner?.name ?? null),
                payout: userWon
                    ? (claimResult?.payout_amount ??
                          wonTicket?.payout_amount ??
                          winner?.payout_amount ??
                          game?.possible_win ??
                          null)
                    : (winner?.payout_amount ?? game?.possible_win ?? null),
                winningCartela: winner?.cartela ?? null,
                winningCard: winner?.card ?? null,
                drawn: drawnSet,
            });
            setLoadingResult(false);
        }, 1200);

        return () => window.clearTimeout(t);
    }, [
        game?.status,
        snapshot?.status,
        winnerSnapshot,
        snapshot?.winner,
        tickets,
        claimResult,
        me,
        game,
        drawnSet,
        resultModal,
    ]);

    const submitBingo = async (): Promise<void> => {
        if (claiming || !tickets?.length) return;
        const active = tickets.filter((t) => t.status === 'active');
        if (active.length === 0) return;

        setClaiming('all');
        setClaimResult(null);
        setError(null);
        setNotWinnerMessage(null);

        let lastReject: ClaimResponse | null = null;
        try {
            for (const ticket of active) {
                try {
                    const res = await api.post<ClaimResponse>(`/games/${numericGameId}/claim`, {
                        ticket_id: ticket.id,
                    });
                    setClaimResult(res.data);
                    if (res.data.status === 'approved' || res.data.status === 'paid') {
                        await refreshMeta();
                        try {
                            hapticFeedback.notificationOccurred('success');
                        } catch {
                            // ignore
                        }
                        return;
                    }
                    lastReject = res.data;
                } catch (e) {
                    const statusBody = (e as { response?: { data?: ClaimResponse & { message?: string } } })
                        .response?.data;
                    if (statusBody?.status === 'approved' || statusBody?.status === 'paid') {
                        setClaimResult(statusBody);
                        await refreshMeta();
                        return;
                    }
                    if (statusBody?.status === 'rejected') {
                        lastReject = statusBody;
                        continue;
                    }
                    const msg = extractMessage(e);
                    // Already won by someone else — stop trying.
                    if (msg.toLowerCase().includes('already') || msg.toLowerCase().includes('won')) {
                        setError(msg);
                        await refreshMeta();
                        return;
                    }
                    // Try next cartela; keep last error for display.
                    lastReject = statusBody?.status ? statusBody : null;
                    if (!statusBody?.status) {
                        setError(msg);
                    }
                }
            }

            if (lastReject?.status === 'rejected') {
                setNotWinnerMessage(
                    lastReject.decision_notes?.trim() ||
                        'No bingo on your cartelas yet — keep marking numbers.',
                );
                try {
                    hapticFeedback.notificationOccurred('error');
                } catch {
                    // ignore
                }
            }
            await refreshMeta();
        } finally {
            setClaiming(null);
        }
    };

    /**
     * Before the round starts, leaving refunds every active cartela (see
     * DELETE /games/{game}/tickets/{ticket}). Once it's Active the round is
     * committed -- leaving just navigates away, no refund.
     */
    const leaveGame = async (): Promise<void> => {
        if (leaving || numericGameId === null) return;

        const canRefund = game?.status === 'draft' || game?.status === 'open';
        const ownActive = tickets?.filter((t) => t.status === 'active') ?? [];

        if (!canRefund || ownActive.length === 0) {
            navigate('/');
            return;
        }

        setLeaving(true);
        try {
            await Promise.all(
                ownActive.map((t) =>
                    api.delete(`/games/${numericGameId}/tickets/${t.id}`).catch(() => undefined),
                ),
            );
        } finally {
            setLeaving(false);
            navigate('/');
        }
    };

    const countdownLabel = gameStatusLine(status, game?.countdown_ends_at, now);
    const latestLetter = lastBall ? letterForNumber(lastBall) : null;

    return (
        <div className="lobby hall w-screen w-hall">
            <ConnectedBar label={connected ? 'Connected' : 'Connecting…'} />

            <div className="w-stats-ribbon">
                <div>
                    <span>Games</span>
                    <strong>{tickets?.length ? 1 : 0}</strong>
                </div>
                <div>
                    <span>Derash</span>
                    <strong>{game ? etbInt(game.possible_win ?? game.prize_pool) : '—'}</strong>
                </div>
                <div>
                    <span>Cards</span>
                    <strong>{game?.ticket_count ?? '—'}</strong>
                </div>
                <div>
                    <span>Bet</span>
                    <strong>{game ? etbInt(game.stake_amount) : '—'}</strong>
                </div>
                <div>
                    <span>Call</span>
                    <strong>{snapshot?.numbers.length ?? 0}</strong>
                </div>
                <div>
                    <span>Bonus</span>
                    <strong>0</strong>
                </div>
                <button
                    type="button"
                    className="w-sound-btn"
                    onClick={() => setSoundOn((v) => !v)}
                    aria-label="Toggle sound"
                >
                    {soundOn ? '🔊' : '🔇'}
                </button>
            </div>

            {(error || liveError) && (
                <div className="lobby-error">
                    <span>{error ?? liveError}</span>
                    {error && (
                        <button type="button" className="lobby-error-x" onClick={() => setError(null)} aria-label="Dismiss">
                            ×
                        </button>
                    )}
                </div>
            )}

            <div className="w-hall-grid">
                <div className="w-hall-left">
                    <div className="w-countdown-box">
                        <div className="w-countdown-head">
                            <span>Countdown</span>
                            <strong>
                                {status === 'active' || status === 'paused' ? 'Running' : countdownLabel}
                            </strong>
                        </div>
                        {lastBall !== null ? (
                            <div className={`w-now-ball w-call-${latestLetter?.toLowerCase() ?? 'b'}`}>
                                {ballLabel(lastBall)}
                            </div>
                        ) : (
                            <div className="w-now-ball w-call-empty">—</div>
                        )}
                        <LastCalls numbers={snapshot?.numbers ?? []} />
                    </div>
                    <MasterBoard drawn={drawnSet} latest={lastBall} />
                </div>

                <div className="w-hall-right">
                    {tickets === null && <div className="lobby-empty">Loading cartela…</div>}

                    {tickets?.length === 0 && (
                        <div className="lobby-empty">
                            No cartela yet. <Link to={`/games/${gameId}/purchase`}>Add Cartela</Link>
                        </div>
                    )}

                    {tickets?.map((ticket) => (
                        <div key={ticket.id} className="w-cartela-wrap">
                            <BingoCardView
                                card={ticket.card}
                                drawn={drawnSet}
                                highlight={ticket.status === 'won'}
                                marked={markedByTicket[ticket.id] ?? new Set()}
                                onToggleMark={(n) => toggleMark(ticket.id, n)}
                            />
                            <div className="w-cartela-foot">Cartela {ticket.serial}</div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="w-hall-actions">
                <div className="w-hall-actions-left">
                    <button type="button" className="w-btn-refresh" onClick={() => void refreshMeta()}>
                        Refresh
                    </button>
                    {game?.status === 'open' && (
                        <Link to={`/games/${gameId}/purchase`} className="w-btn-add">
                            Add Cartela
                        </Link>
                    )}
                    <button
                        type="button"
                        className="w-btn-leave"
                        disabled={leaving}
                        onClick={() => void leaveGame()}
                    >
                        {leaving ? 'Leaving…' : 'Leave Game'}
                    </button>
                </div>
                <button
                    type="button"
                    className="w-btn-bingo"
                    disabled={!canClaim || !tickets?.some((t) => t.status === 'active') || claiming !== null}
                    onClick={() => void submitBingo()}
                >
                    {claiming ? 'Checking…' : 'Bingo'}
                </button>
            </div>

            {loadingResult && (game?.status === 'completed' || snapshot?.status === 'completed') && (
                <div className="w-complete-overlay">
                    <div className="w-complete-box">
                        <div className="w-spinner" />
                        <strong>Game complete</strong>
                        <p>Loading result…</p>
                    </div>
                </div>
            )}

            {resultModal && (
                <GameResultModal
                    result={resultModal}
                    stakeMinor={game?.stake_amount ?? null}
                    onClose={() => setResultModal(null)}
                />
            )}

            {notWinnerMessage && (
                <NotAWinnerModal
                    message={notWinnerMessage}
                    onClose={() => setNotWinnerMessage(null)}
                />
            )}
        </div>
    );
}

function extractMessage(error: unknown): string {
    if (typeof error === 'object' && error !== null && 'response' in error) {
        const res = (error as { response?: { data?: { message?: string } } }).response;
        if (res?.data?.message) return res.data.message;
    }
    return error instanceof Error ? error.message : 'Unknown error';
}
